import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles/MyBillsTable.module.scss';

import LSMBillsTableRow from './LSMBillsTableRow';

// Types.
import { InvoiceLSM, lsmBillsTableTypes, lsmTableSorterConfig, lsmTableSorter } from '../../types/billing';

// https://www.smashingmagazine.com/2020/03/sortable-tables-react/

const useSortableData = (items: InvoiceLSM[]): lsmTableSorter => {
  const [sortConfig, setSortConfig] = React.useState<lsmTableSorterConfig>();

  const sortedItems = React.useMemo((): InvoiceLSM[] => {
    const sortableItems = [...items];
    if (sortConfig !== undefined) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key: keyof InvoiceLSM): void => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return { items: sortedItems, requestSort, sortConfig };
};

const LSMBillsTable: React.FunctionComponent<lsmBillsTableTypes> = (props: lsmBillsTableTypes) => {
  const { t } = useTranslation();

  const { invoices, desiredPdf, setDesiredPdf } = props;
  const { items, requestSort, sortConfig } = useSortableData(invoices);

  const getClassNamesFor = (name: keyof InvoiceLSM): string | undefined => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  return (
    <div className="col table-responsive">
      <div className="title">
        <h2>{t('billing:billing.billingTableLSM')}</h2>
      </div>
      <table id="lsmBills" className={styles.table}>
        <tbody>
          <tr>
            <th onClick={(): void => requestSort('dueDate')}>
              {t('billing:billing.sortDueDate')}
              <span className={getClassNamesFor('dueDate')} />
            </th>
            <th onClick={(): void => requestSort('invoiceNo')}>
              {t('billing:billing.sortContractNumber')}
              <span className={getClassNamesFor('LSM_contractNo')} />
            </th>
            <th onClick={(): void => requestSort('invoiceNo')}>
              {t('billing:billing.sortBillNumber')}
              <span className={getClassNamesFor('LSM_invoiceNo')} />
            </th>
            <th onClick={(): void => requestSort('invoiceSum')}>
              {t('billing:billing.sortBillSum')}
              <span className={getClassNamesFor('invoiceSum')} />
            </th>
            <th>{t('billing:billing.sortPdf')}</th>
          </tr>
          {items &&
            items.map((invoice) => (
              <LSMBillsTableRow
                key={`${invoice.LSM_invoiceNo}_${invoice.LSM_contractNo}_${invoice.LSM_LedgerNo}`}
                invoice={invoice}
                desiredPdf={desiredPdf}
                setDesiredPdf={setDesiredPdf}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default LSMBillsTable;
